import { useState, useEffect } from 'react'
import { useParams, useLocation, useSearchParams } from 'react-router-dom'
import { getUserById } from 'apis/authApi'
import { getCardById } from 'apis/cardApi'

/**
 * Custom hook to fetch member data based on route parameters
 * Handles different routes: add-card, card view, and member edit
 * @param {Object} currentUser - The current authenticated user
 * @returns {Object} { memberData, loading }
 */
export const useMemberData = (currentUser) => {
    const { cardId } = useParams()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const [memberData, setMemberData] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchCardOwnerData = async () => {
            // For add-card route, we need to determine the target member
            // The route is /team/members/card/add-card/:cardId
            // If cardId is a temp UUID, we're creating a NEW card for the current member being viewed

            try {
                // Check if this is the add-card route (creating a new card)
                const isAddCardRoute = location?.pathname?.includes('/add-card')

                if (isAddCardRoute) {
                    // Check if we have memberUserId from navigation state or URL params
                    const memberUserId = location?.state?.memberUserId || searchParams.get('memberUserId')

                    if (memberUserId) {
                        const userResponse = await getUserById(memberUserId)
                        if (userResponse && userResponse.data) {
                            setMemberData(userResponse.data)
                            setLoading(false)
                            return
                        }
                    }
                    // Fallback to current user if no memberUserId
                    setMemberData(currentUser)
                    setLoading(false)
                    return
                } else if (!cardId) {
                    // No cardId - use current user
                    setMemberData(currentUser)
                    setLoading(false)
                    return
                } else {
                    // Regular card view route - get owner from card
                    const cardResponse = await getCardById(cardId)

                    if (cardResponse && cardResponse.card) {
                        const cardOwnerUserId = cardResponse.card.userId

                        const userResponse = await getUserById(cardOwnerUserId)

                        if (userResponse && userResponse.data) {
                            setMemberData(userResponse.data)
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch card/member data:', error)
                // Fallback to current user on error
                setMemberData(currentUser)
            } finally {
                setLoading(false)
            }
        }

        fetchCardOwnerData()
    }, [cardId, currentUser, location, searchParams])

    // Use memberData if available (for card owner), otherwise currentUser (for own cards)
    const displayUser = memberData || currentUser

    return { displayUser, loading }
}

